// @ts-nocheck
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Divider,
  Typography,
  Box,
  Stack,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import { string, mixed, object } from 'yup';
import moment from 'moment';
import PropTypes from 'prop-types';

import DatePicker from '../../DatePicker';
import swalToast from '../../../../components/Common/swalToast';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import { getErrorHelperText } from '../../../../utils/Utils';
import { NOOP, YYYY_MM_DD_DATE_FORMAT } from '../../../../const/CommonConst';

const OverrideAttributeModal = ({
  open,
  onClose,
  attribute,
  handleClose,
  entityData,
  overrideData,
  formikForm,
  refreshSingleAttribute,
  getOverrideData,
  handleGetSectionPermission,
  getAttributeMapping,
  handleAttributeValueChange = NOOP,
}) => {
  const { overrideAttributeValue } = useAttribute();

  const validationSchema = object().shape({
    overrideValue: mixed().required('Value is required'),
    justification: string()
      .trim()
      .required('Override Rationale is required')
      .max(250, 'Only 250 characters are allowed'),
  });

  const formik = useFormik({
    initialValues: {
      overrideValue: '',
      justification: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const payload = {
        entityId: entityData?.entity_id,
        entityType: entityData?.entity_type,
        attributeId: attribute?.id || '',
        overrided_value:
          attribute?.data_type === 'Date'
            ? moment(values?.overrideValue).format(YYYY_MM_DD_DATE_FORMAT)
            : values?.overrideValue,

        justification: values?.justification,
      };

      try {
        setSubmitting(true);
        const res = await overrideAttributeValue(payload);
        // if the API returned success update the parent form and show toast
        if (res?.data) {
          formikForm?.setFieldValue(
            attribute?.data_type === 'Date'
              ? moment(values?.overrideValue).format(YYYY_MM_DD_DATE_FORMAT)
              : values?.overrideValue || null
          );
          swalToast({
            icon: 'success',
            title: res?.data?.msg || 'Override saved successfully',
          });
          await refreshSingleAttribute(
            attribute?.id,
            formik?.values?.overrideValue
          );
          // Notify constraint system about the updated value
          const newValue =
            attribute?.data_type === 'Date'
              ? moment(values?.overrideValue).format(YYYY_MM_DD_DATE_FORMAT)
              : values?.overrideValue || null;
          const oldValue = formikForm?.initialValues?.[attribute?.id];

          if (handleAttributeValueChange) {
            handleAttributeValueChange(attribute?.id, newValue, oldValue);
          }

          await getOverrideData();
          await getAttributeMapping();
          await handleGetSectionPermission({
            entityId: entityData?.entity_id,
            entityType: entityData?.entity_type,
          });
          handleClose?.();
          onClose();
        } else {
          swalToast({
            icon: 'error',
            title: 'Something went wrong while saving override',
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const matchedOverride = useMemo(() => {
    return overrideData?.find?.((item) => item?.attributeId === attribute?.id);
  }, [overrideData, attribute?.id]);

  const setDefaultOverrideValues = useCallback(() => {
    if (!matchedOverride) {
      return;
    }
    formik?.setFieldValue(
      'justification',
      matchedOverride?.justification || ''
    );

    if (attribute?.data_type === 'Date' && matchedOverride?.attribute_value) {
      formik?.setFieldValue(
        'overrideValue',
        moment(matchedOverride?.attribute_value)
      );
    } else {
      formik?.setFieldValue(
        'overrideValue',
        matchedOverride?.attribute_value || ''
      );
    }
  }, [matchedOverride, attribute?.data_type, formik?.setFieldValue]);

  useEffect(() => {
    if (open) {
      setDefaultOverrideValues();
    }
  }, [open]);

  const handleOverrideChange = useCallback(
    (type = 'text') =>
      (eOrDate) => {
        if (!formik) return;
        switch (type) {
          case 'date':
            formik.setFieldValue('overrideValue', moment(eOrDate));
            break;
          case 'text':
          default: {
            const val = eOrDate?.target?.value;

            let parsed = val;

            if (attribute?.data_type === 'Integer') {
              parsed = val ? parseInt(val, 10) : '';
            } else if (attribute?.data_type === 'Decimal') {
              parsed = val ? parseFloat(val) : '';
            }

            formik.setFieldValue('overrideValue', parsed);
            break;
          }
        }
      },
    [formik]
  );

  const renderField = useMemo(() => {
    const dataType = attribute?.data_type;

    const errorText = getErrorHelperText(
      formik?.errors?.overrideValue,
      formik?.touched?.overrideValue
    );

    switch (dataType) {
      case 'Date':
        return (
          <DatePicker
            label={attribute?.label || attribute?.name || 'Date'}
            value={
              formik?.values?.overrideValue
                ? moment(formik?.values?.overrideValue)
                : null
            }
            onChange={handleOverrideChange('date')}
            slotProps={{
              field: { clearable: true },
            }}
            fullWidth
            error={!!errorText}
            helperText={errorText}
          />
        );

      case 'Integer':
      case 'Decimal':
        return (
          <TextField
            fullWidth
            type="number"
            label={attribute?.label || attribute?.name || 'Number'}
            value={formik?.values?.overrideValue}
            onChange={handleOverrideChange('text')}
            error={!!errorText}
            helperText={errorText}
            placeholder="Enter numeric value"
          />
        );

      case 'Choice':
      case 'Dropdown':
        return (
          <TextField
            select
            fullWidth
            size="small"
            label={attribute?.label || attribute?.name || 'Select'}
            value={formik?.values?.overrideValue}
            onChange={handleOverrideChange('text')}
            error={!!errorText}
            helperText={errorText}
          >
            {attribute?.drop_down_options?.length > 0 ? (
              attribute?.drop_down_options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No options available</MenuItem>
            )}
          </TextField>
        );

      default:
        return (
          <TextField
            fullWidth
            label={attribute?.label || attribute?.name || 'Value'}
            value={formik?.values?.overrideValue}
            onChange={handleOverrideChange('text')}
            error={!!errorText}
            helperText={errorText}
            placeholder="Enter value"
          />
        );
    }
  }, [attribute, formik, handleOverrideChange]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box component="span" aria-live="polite" aria-atomic="true">
          Override {attribute?.label || attribute?.name}
        </Box>
      </DialogTitle>

      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
        }}
        title="Close Override Dialog"
      >
        <CloseIcon />
      </IconButton>
      <Divider />
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body1">
            This override will replace the calculated outcome with your selected
            option and freeze any related records.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          {renderField}

          {/*  Override Rationale */}
          <Box>
            <Typography variant="body2">Override Rationale</Typography>
            <TextField
              multiline
              rows={3}
              placeholder="Enter override rationale..."
              fullWidth
              name="justification"
              value={formik?.values?.justification}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              error={getErrorHelperText(
                formik?.errors?.justification,
                formik?.touched?.justification
              )}
              helperText={getErrorHelperText(
                formik?.errors?.justification,
                formik?.touched?.justification
              )}
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 2, py: 2 }}>
        <Stack direction="row" spacing={2}>
          <Button onClick={onClose} variant="outlined" title="Cancel">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik?.isSubmitting}
            onClick={formik?.handleSubmit}
            title="Save"
          >
            {formik?.isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

OverrideAttributeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attribute: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  handleClose: PropTypes.func,
  entityData: PropTypes.oneOfType([PropTypes.object]).isRequired,
  overrideData: PropTypes.oneOfType([PropTypes.object]).isRequired,
  formikForm: PropTypes.oneOfType([PropTypes.object]).isRequired,
  refreshSingleAttribute: PropTypes.func.isRequired,
  getOverrideData: PropTypes.func.isRequired,
  getAttributeMapping: PropTypes.func.isRequired,
  handleGetSectionPermission: PropTypes.func.isRequired,
  handleAttributeValueChange: PropTypes.func,
};

export default OverrideAttributeModal;
